import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { deleteEvent, updateEvent } from "../../lib/api-event";
import type { EventType, FamilyEvent, UpdateEventRequest } from "../../types/event";
import type { FamilyMember } from "../../types/family";

const types: Array<{ value: EventType; label: string }> = [
  { value: "birthday", label: "День рождения" },
  { value: "anniversary", label: "Годовщина" },
  { value: "holiday", label: "Праздник" },
  { value: "other", label: "Другое" },
];

type Complete = (error?: unknown) => Promise<void>;

function useModal(pending: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    return () => {
      if (dialog.open && typeof dialog.close === "function") dialog.close();
    };
  }, []);
  const onCancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    if (!pending) onClose();
  };
  return { ref, onCancel };
}

function moscowToday(): string {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Europe/Moscow", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}-${value("day")}`;
}

export function EditEventDialog({
  event, members, onClose, onComplete,
}: {
  event: FamilyEvent;
  members: FamilyMember[];
  onClose: () => void;
  onComplete: Complete;
}) {
  const titleID = useId();
  const headingID = useId();
  const [title, setTitle] = useState(event.title);
  const [type, setType] = useState(event.type);
  const [date, setDate] = useState(event.date);
  const [description, setDescription] = useState(event.description ?? "");
  const [memberID, setMemberID] = useState(event.member?.id ?? "");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modal = useModal(pending, onClose);
  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();
  const normalizedDescription = trimmedDescription || null;
  const normalizedMember = memberID || null;
  const changed =
    trimmedTitle !== event.title || type !== event.type || date !== event.date ||
    normalizedDescription !== (event.description ?? null) ||
    normalizedMember !== (event.member?.id ?? null);
  const validDate = date === event.date || date >= moscowToday();
  const canSubmit = changed && trimmedTitle.length > 0 && validDate && !pending;

  async function submit(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (!canSubmit) {
      setError(validDate ? "Измените хотя бы одно поле." : "Прошедшую дату выбрать нельзя.");
      return;
    }
    const patch: UpdateEventRequest = {};
    if (trimmedTitle !== event.title) patch.title = trimmedTitle;
    if (type !== event.type) patch.type = type;
    if (date !== event.date) patch.date = date;
    if (normalizedDescription !== (event.description ?? null)) patch.description = normalizedDescription;
    if (normalizedMember !== (event.member?.id ?? null)) patch.member_id = normalizedMember;
    setPending(true);
    setError(null);
    try {
      await updateEvent(event.id, patch);
      await onComplete();
    } catch (mutationError) {
      await onComplete(mutationError);
    }
  }

  const inputClass = "form-control mt-2 w-full rounded-2xl border border-line bg-white px-4 py-3 disabled:cursor-not-allowed";
  return (
    <dialog ref={modal.ref} onCancel={modal.onCancel} aria-labelledby={headingID} className="m-auto max-h-[94vh] w-[calc(100%_-_2rem)] max-w-xl overflow-y-auto rounded-[28px] bg-white p-6 text-content shadow-2xl backdrop:bg-black/40">
      <h2 id={headingID} className="text-2xl font-extrabold">Редактировать событие</h2>
      <form onSubmit={(formEvent) => void submit(formEvent)} noValidate className="mt-5 grid gap-4 sm:grid-cols-2">
        <label htmlFor={titleID} className="text-sm font-bold sm:col-span-2">Название<input id={titleID} autoFocus value={title} onChange={(e) => { setTitle(e.target.value); setError(null); }} maxLength={255} disabled={pending} className={inputClass} /></label>
        <label className="text-sm font-bold">Тип<select aria-label="Тип" value={type} onChange={(e) => setType(e.target.value as EventType)} disabled={pending} className={inputClass}>{types.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
        <label className="text-sm font-bold">Дата<input aria-label="Дата" type="date" min={moscowToday()} value={date} onChange={(e) => setDate(e.target.value)} disabled={pending} className={inputClass} /></label>
        <label className="text-sm font-bold">Участник<select aria-label="Участник" value={memberID} onChange={(e) => setMemberID(e.target.value)} disabled={pending} className={inputClass}><option value="">Общее событие</option>{members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}</select></label>
        <label className="text-sm font-bold sm:col-span-2">Описание (опционально)<textarea aria-label="Описание (опционально)" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} rows={3} disabled={pending} className={inputClass} /></label>
        {error && <div role="alert" className="rounded-xl bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#B91C1C] sm:col-span-2">{error}</div>}
        <div className="flex flex-col-reverse gap-3 sm:col-span-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={onClose} disabled={pending} className="h-11 rounded-xl bg-surface-subtle px-5 text-sm font-bold">Отмена</button>
        <button type="submit" disabled={!canSubmit} className="h-11 rounded-xl bg-action px-5 text-sm font-bold text-white disabled:bg-surface-disabled">{pending ? "Сохраняем…" : "Сохранить"}</button>
        </div>
      </form>
    </dialog>
  );
}

export function DeleteEventDialog({
  event, onClose, onComplete,
}: {
  event: FamilyEvent;
  onClose: () => void;
  onComplete: Complete;
}) {
  const headingID = useId();
  const [pending, setPending] = useState(false);
  const modal = useModal(pending, onClose);
  async function confirm() {
    setPending(true);
    try {
      await deleteEvent(event.id);
      await onComplete();
    } catch (mutationError) {
      await onComplete(mutationError);
    }
  }
  return (
    <dialog ref={modal.ref} onCancel={modal.onCancel} aria-labelledby={headingID} className="m-auto w-[calc(100%_-_2rem)] max-w-sm rounded-[28px] bg-white p-6 text-center text-content shadow-2xl backdrop:bg-black/40">
      <h2 id={headingID} className="text-2xl font-extrabold">{pending ? "Удаляем…" : "Удалить событие?"}</h2>
      <p className="mt-3 text-sm leading-6 text-muted">{pending ? "Пожалуйста, подождите." : <>«{event.title}» исчезнет, но желания и бронирования сохранятся.</>}</p>
      <div className="mt-6 flex flex-col gap-3">
        <button type="button" onClick={() => void confirm()} disabled={pending} className="h-12 rounded-xl bg-danger text-sm font-bold text-white disabled:bg-[#FCA5A5]">{pending ? "Удаляем…" : "Удалить"}</button>
        <button type="button" onClick={onClose} disabled={pending} className="h-12 rounded-xl bg-surface-subtle text-sm font-bold">Отмена</button>
      </div>
    </dialog>
  );
}
